import React from "react";

/**
 * Dany's words, rendered.
 *
 * PORT NOTE (DY1.4): new — the design system renders a turn's text as a single
 * `<p>`, which is right for the specimen copy it ships with and wrong for a live
 * model. Even told not to (prompt v1.1.0, `_SURFACE`), a model reaches for
 * `**bold**` often enough that a visitor sees literal asterisks around a
 * company name. The prompt is the root fix; this is the one that makes a leak
 * look deliberate instead of broken.
 *
 * Deliberately NOT a markdown library. `react-markdown` plus a sanitiser is
 * ~40kB to render a surface whose whole design rule is "the assistant's words
 * are body copy, not a document" — headings, tables and code fences have no
 * place in a turn and rendering them would invite the model to use them. This
 * handles exactly the inline subset a chat model actually emits, and anything
 * else falls through as the plain text it already is.
 *
 * Supported: `**bold**`, `*italic*` / `_italic_`, `` `code` ``, blank-line
 * paragraphs, single newlines as line breaks, and — since prompt v1.2.0 — a run
 * of numbered lines as an ordered list of steps. A leading `- ` or `#` is still
 * stripped: bullets and headings remain banned, and a stray dash reads as a
 * typo, not as structure.
 *
 * **Why numbered lists specifically.** A sequential process (how to sign up,
 * how to post a job, how to send a quote) delivered as one paragraph is a wall:
 * the reader has to re-parse the sentence to find where step three starts, and
 * on a phone that paragraph is nine lines tall. Steps are the one shape whose
 * meaning IS its layout, so it is the one shape the prose renderer keeps. Every
 * other list — options, features, categories, FixyBees — is either a sentence or
 * a widget, because those the tools own.
 */

// One pass, alternation ordered so `**` wins over `*`.
const INLINE = /(\*\*[^*\n]+\*\*|__[^_\n]+__|\*[^*\n]+\*|_[^_\n]+_|`[^`\n]+`)/g;

const BOLD = { fontWeight: "var(--weight-semibold)" };
const ITALIC = { fontStyle: "italic" };
const CODE = {
  fontFamily: "var(--font-mono)",
  fontSize: "0.92em",
  background: "var(--surface-muted)",
  borderRadius: 4,
  padding: "1px 5px",
};

function inline(text, keyPrefix) {
  const parts = String(text).split(INLINE);
  return parts.map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if (!part) return null;
    if (part.length > 4 && part.startsWith("**") && part.endsWith("**")) {
      return <strong key={key} style={BOLD}>{part.slice(2, -2)}</strong>;
    }
    if (part.length > 4 && part.startsWith("__") && part.endsWith("__")) {
      return <strong key={key} style={BOLD}>{part.slice(2, -2)}</strong>;
    }
    if (part.length > 2 && part.startsWith("*") && part.endsWith("*")) {
      return <em key={key} style={ITALIC}>{part.slice(1, -1)}</em>;
    }
    if (part.length > 2 && part.startsWith("_") && part.endsWith("_")) {
      return <em key={key} style={ITALIC}>{part.slice(1, -1)}</em>;
    }
    if (part.length > 2 && part.startsWith("`") && part.endsWith("`")) {
      return <code key={key} style={CODE}>{part.slice(1, -1)}</code>;
    }
    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
}

/** Strip the block syntax the surface does not render, leaving the words. */
function clean(line) {
  return line
    .replace(/^\s{0,3}#{1,6}\s+/, "")      // heading marker
    .replace(/^\s{0,3}[-*+]\s+/, "")       // bullet marker
    .replace(/^\s{0,3}\d+[.)]\s+/, "")     // ordered marker
    .replace(/^\s{0,3}>\s?/, "");          // quote marker
}

// "1. ", "2) " — up to two digits, because a process with a hundred steps is
// not a process. Requires the space, so a price ("$1.500") or a date is not a
// step.
const STEP = /^\s{0,3}(\d{1,2})[.)]\s+(.*)$/;

/**
 * Group the lines into blocks before rendering any of them.
 *
 * Line-based rather than paragraph-based on purpose: models write steps both
 * tightly (`1.\n2.\n3.`) and loosely (a blank line between each), and the reader
 * means the same thing either way. A blank line only ends a list if what follows
 * is not another step.
 *
 * A single numbered line is NOT a list. "1. " on its own is either a false
 * positive or a step with nothing to be sequential against; it falls back to a
 * paragraph with the marker stripped, which is what it did before v1.2.0.
 */
function blocks(source) {
  const out = [];
  const lines = source.split("\n");
  let paragraph = [];
  let steps = null;

  const flushParagraph = () => {
    if (paragraph.length) out.push({ type: "p", lines: paragraph });
    paragraph = [];
  };
  const flushSteps = () => {
    if (!steps) return;
    if (steps.length > 1) out.push({ type: "ol", items: steps });
    // One step is not a sequence — replay it as the paragraph it really is.
    else if (steps.length === 1) paragraph.push(steps[0]);
    steps = null;
  };

  for (const raw of lines) {
    const line = raw.trim();
    const step = line.match(STEP);
    if (step) {
      flushParagraph();
      (steps = steps || []).push(step[2].trim());
      continue;
    }
    if (!line) {
      // Held open: the blank may be the gap between two steps. Only a real line
      // of prose closes the list.
      if (!steps) flushParagraph();
      continue;
    }
    flushSteps();
    paragraph.push(line);
  }
  flushSteps();
  flushParagraph();
  return out;
}

const MARKER = {
  flex: "0 0 auto", width: 22, height: 22, borderRadius: "var(--radius-pill)",
  background: "var(--hive-a20)", color: "var(--hive-ink)",
  fontFamily: "var(--font-mono)", fontSize: "var(--text-2xs)",
  fontWeight: "var(--weight-semibold)", lineHeight: "22px", textAlign: "center",
  marginTop: 1,
};

/**
 * Lone `{word}` tokens, which are never something Dany meant to say.
 *
 * Jorge saw `{goodbye}` rendered at the end of a conversation on 2026-08-19. It
 * is in no i18n bundle, no prompt of ours, no component, no row of
 * `dany_messages`, and not in the live ElevenLabs agent config — every one of
 * those was searched. What remains is a model emitting a stage direction it
 * invented, most plausibly the voice LLM reading the CLOSING block's "say the
 * goodbye" as a slot to fill.
 *
 * So this is a guard, not a fix for a located cause, and it is written to be
 * narrow: a single brace pair around one lowercase identifier, alone on its
 * line or fenced by whitespace. Real prose does not contain those, and prices,
 * ZIPs and company names cannot match. Anything with spaces, punctuation or
 * capitals inside is left exactly as the model wrote it.
 */
const STAGE_DIRECTION = /\s*\{[a-z][a-z0-9_]{2,}\}(?=\s|$)/g;

export function DanyProse({ text, style = {}, steps = true }) {
  const source = String(text || "").replace(STAGE_DIRECTION, "").trim();
  if (!source.trim()) return null;

  // Streaming means the text arrives mid-token: a half-written `**Miami` must
  // not render its asterisks and must not be treated as an unterminated span
  // that swallows the rest of the turn. Splitting on the closed-pair regex does
  // both — an unclosed marker stays literal until its partner arrives, which
  // for two characters at the very end of the visible text is invisible.
  const parsed = steps
    ? blocks(source)
    // The caller can turn steps off for a turn where numbered lines cannot mean
    // steps — see `DanyTurn`'s `steps` prop. The markers are still stripped, so
    // the fallback is prose, not raw "1.".
    : blocks(source).map((b) => (b.type === "ol" ? { type: "p", lines: b.items } : b));

  return (
    <div style={{
      fontFamily: "var(--font-body)", fontSize: "var(--text-base)",
      lineHeight: "var(--leading-relaxed)", color: "var(--dany-text)",
      maxWidth: "var(--measure-body)", textWrap: "pretty",
      // A grid column defaults to `auto`, whose minimum is min-content — so one
      // unbreakable token (a URL, an email, a long ES compound) widened this
      // column past the page padding and Dany's text ran to the physical edge
      // of an Android screen while the widgets, which size with
      // `minmax(min(100%, N), 1fr)`, stayed inside it. `minmax(0, 1fr)` caps
      // the column at the container; `anywhere` — not `break-word` — is what
      // also lowers the intrinsic minimum, so the two work together.
      display: "grid", gridTemplateColumns: "minmax(0, 1fr)",
      gap: "var(--space-3)", minWidth: 0, overflowWrap: "anywhere",
      ...style,
    }}>
      {parsed.map((block, bi) => (block.type === "ol" ? (
        // The number is the index, not what the model typed: a model that
        // restarts at 1 halfway down, or writes 1/2/2/3, still reads as steps.
        <ol key={bi} role="list" style={{
          display: "grid", gridTemplateColumns: "minmax(0, 1fr)",
          gap: "var(--space-3)", listStyle: "none",
          margin: 0, padding: 0, minWidth: 0,
        }} data-testid="dany-steps">
          {block.items.map((item, ii) => (
            <li key={ii} style={{ display: "flex", gap: "var(--space-3)", alignItems: "flex-start" }}>
              {/* Not aria-hidden: `list-style: none` costs the ordinal a
                  screen reader would otherwise announce, so this span IS the
                  number rather than a decoration of it. */}
              <span style={MARKER}>{ii + 1}</span>
              <span style={{ flex: 1, minWidth: 0 }}>{inline(clean(item), `${bi}-${ii}`)}</span>
            </li>
          ))}
        </ol>
      ) : (
        <p key={bi}>
          {block.lines.map((line, li, all) => (
            <React.Fragment key={li}>
              {inline(clean(line), `${bi}-${li}`)}
              {li < all.length - 1 && <br />}
            </React.Fragment>
          ))}
        </p>
      )))}
    </div>
  );
}

export default DanyProse;
